import { useEffect, useRef, useState } from "react";
import { APIProvider, Map, useApiIsLoaded } from "@vis.gl/react-google-maps";
import MarkerWithInfo from "../map-components/MarkerWithInfo.jsx";

const GOOGLE_MAPS_API_KEY = "AIzaSyDgzWSot4NiqhlW2QyRYi1TtAM97nrrvq0";
const DEFAULT_CENTER = { lat: 47.0379, lng: -122.9007 };
const DEFAULT_ZOOM = 15;

function StreetViewPanorama({ position }) {
  const streetViewRef = useRef(null);
  const isLoaded = useApiIsLoaded();
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!isLoaded || !streetViewRef.current || !position) {
      return;
    }

    let isMounted = true;
    let panorama = null;
    let statusListener = null;

    setStatus("loading");

    try {
      const service = new window.google.maps.StreetViewService();

      service.getPanorama({ location: position, radius: 100 }, (data, serviceStatus) => {
        if (!isMounted || !streetViewRef.current) {
          return;
        }

        if (
          serviceStatus !== window.google.maps.StreetViewStatus.OK ||
          !data?.location?.pano
        ) {
          setStatus("not-found");
          return;
        }

        try {
          panorama = new window.google.maps.StreetViewPanorama(streetViewRef.current, {
            pano: data.location.pano,
            position,
            pov: { heading: 0, pitch: 0 },
            zoom: 1,
            addressControl: true,
            fullscreenControl: true,
            motionTracking: false,
          });

          statusListener = panorama.addListener("status_changed", () => {
            if (!isMounted) {
              return;
            }

            const panoramaStatus = panorama.getStatus();

            if (panoramaStatus === window.google.maps.StreetViewStatus.OK) {
              setStatus("ready");
            } else if (panoramaStatus === window.google.maps.StreetViewStatus.ZERO_RESULTS) {
              setStatus("not-found");
            } else {
              setStatus("error");
            }
          });

          setStatus("ready");
        } catch {
          setStatus("error");
        }
      });
    } catch {
      setStatus("error");
    }

    return () => {
      isMounted = false;

      if (statusListener) {
        statusListener.remove();
      }

      if (panorama) {
        panorama.setVisible(false);
      }

      if (streetViewRef.current) {
        streetViewRef.current.innerHTML = "";
      }
    };
  }, [isLoaded, position]);

  return (
    <div style={{ height: "400px", width: "100%", position: "relative" }}>
      <div
        ref={streetViewRef}
        style={{
          height: "100%",
          width: "100%",
          display: status === "ready" ? "block" : "none",
        }}
      />

      {status === "not-found" ? (
        <div className="p-3">
          Street View imagery is not available for this stop location.
        </div>
      ) : null}

      {status === "error" ? (
        <div className="p-3">
          Unable to render Street View right now. Please refresh and try again.
        </div>
      ) : null}
    </div>
  );
}

export function StreetViewMap({ stop }) {
  const hasCoordinates = Boolean(stop?.stop_lat && stop?.stop_lon);

  if (!hasCoordinates) {
    return <div className="p-3">Street View is unavailable: stop coordinates are missing.</div>;
  }

  const position = {
    lat: Number(stop.stop_lat),
    lng: Number(stop.stop_lon),
  };

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <StreetViewPanorama position={position} />
    </APIProvider>
  );
}

export default function StopMap({ stop }) {
  const [hasError, setHasError] = useState(false);
  const [openInfoId, setOpenInfoId] = useState(null);

  const hasCoordinates = Boolean(stop?.stop_lat && stop?.stop_lon);

  if (!hasCoordinates) {
    return null;
  }



  const position = {
    lat: Number(stop.stop_lat),
    lng: Number(stop.stop_lon),
  };

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      {hasError ? (
        <div className="p-3">
          Error loading map data. Please refresh your browser.
        </div>
      ) : (
        <Map
          mapId="stop-map"
          style={{ height: "400px", width: "100%" }}
          defaultCenter={position || DEFAULT_CENTER}
          defaultZoom={DEFAULT_ZOOM}
          gestureHandling="greedy"
          onError={() => setHasError(true)}
        >
          <MarkerWithInfo
            markerId={`stop-${stop.stop_id}-1`}
            openId={openInfoId}
            setOpenId={setOpenInfoId}
            position={position}
            label={stop.stop_name}
            infoContent={
              <div>
                <strong>{stop.stop_name}</strong>
              </div>
            }
          />
        </Map>
      )}
    </APIProvider>
  );
}
